import React from "react";
import {
    AnyFilterTypes,
    AnyOfferTypes,
    BrokerOffer,
    CommercialFilter,
    CommercialOffer,
    Filter,
    FilterTypes,
    FlatOffer,
    FlatsFilter,
    HouseOffer,
    HousesFilter,
    LandOffer,
    LandsFilter,
    OfferType,
    OthersFilter,
    PropertyTypes,
} from "Types";

type AppState = {
    filter: {
        [FilterTypes.flats]: FlatsFilter;
        [FilterTypes.houses]: HousesFilter;
        [FilterTypes.lands]: LandsFilter;
        [FilterTypes.commercials]: CommercialFilter;
        [FilterTypes.others]: OthersFilter;
    };
    offers: {
        [FilterTypes.flats]: FlatOffer[];
        [FilterTypes.houses]: HouseOffer[];
        [FilterTypes.lands]: LandOffer[];
        [FilterTypes.commercials]: CommercialOffer[];
        [FilterTypes.others]: BrokerOffer[];
    };
};

export const initialState: AppState = {
    filter: {
        [FilterTypes.flats]: {
            offerType: OfferType.all,
            typeOfProperty: PropertyTypes.flats,
        },
        [FilterTypes.houses]: {
            offerType: OfferType.all,
            typeOfProperty: PropertyTypes.houses,
        },
        [FilterTypes.lands]: {
            offerType: OfferType.all,
            typeOfProperty: PropertyTypes.lands,
        },
        [FilterTypes.commercials]: {
            offerType: OfferType.all,
            typeOfProperty: undefined,
            typeOfProperties: [
                PropertyTypes.commercialObjects,
                PropertyTypes.housesToRent,
                PropertyTypes.hospitality,
                PropertyTypes.commercialSpaces,
            ],
        },
        [FilterTypes.others]: {
            offerType: OfferType.all,
            typeOfProperty: undefined,
            isOther: true,
            typeOfProperties: [],
        },
    },
    offers: {
        [FilterTypes.flats]: [],
        [FilterTypes.houses]: [],
        [FilterTypes.lands]: [],
        [FilterTypes.commercials]: [],
        [FilterTypes.others]: [],
    },
};

export enum StateActions {
    setOffers,
    setFilter,
}

export type Action =
    | {
          type: StateActions.setOffers;
          payload: { type: FilterTypes; offers: BrokerOffer[] };
      }
    | {
          type: StateActions.setFilter;
          payload: { type: FilterTypes; filter: Filter };
      };

interface StoreApi {
    state: AppState;
    dispatch: React.Dispatch<Action>;
}

const getInitialState = (): AppState => {
    return initialState;
};

const context = React.createContext<StoreApi>({
    state: getInitialState(),
    dispatch: () => Object,
});

function reducer(state: AppState, action: Action): AppState {
    switch (action.type) {
        case StateActions.setOffers:
            return {
                ...state,
                offers: {
                    ...state.offers,
                    [action.payload.type]: action.payload.offers,
                },
            };
        case StateActions.setFilter: {
            return {
                offers: {
                    ...state.offers,
                    [action.payload.type]: [],
                },
                filter: {
                    ...state.filter,
                    [action.payload.type]: action.payload.filter,
                },
            };
        }
        default:
            throw new Error();
    }
}

const Provider: React.FC = ({ children }) => {
    const [state, dispatch] = React.useReducer<React.Reducer<AppState, Action>>(reducer, getInitialState());
    return <context.Provider value={{ state, dispatch }}>{children}</context.Provider>;
};

export const useContextState = (): {
    state: AppState;
    dispatch: React.Dispatch<Action>;
} => {
    const c = React.useContext(context);
    if (c === undefined) throw new Error(`No provider for AppContext given`);
    return c;
};

export const useOfferTypeState = <TOffers extends AnyOfferTypes[], TFilter extends AnyFilterTypes>(
    type: FilterTypes
): { offers: TOffers; filter: TFilter; dispatch: React.Dispatch<Action> } => {
    const { state, dispatch } = useContextState();
    const filter = state.filter[type] as TFilter;
    const offers = state.offers[type] as TOffers;
    return {
        offers,
        filter,
        dispatch,
    };
};

export { Provider };
